<template>
  <div class="modal fade registrar-proveedor-modal" :class="{ show: showModal }" :style="{ display: showModal ? 'block' : 'none' }" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-xl" role="document">
      <div class="modal-content">
        <div class="modal-header bg-success text-white">
          <h5 class="modal-title">
            <i :class="isEditMode ? 'fas fa-truck-loading me-2' : 'fas fa-plus-circle me-2'"></i>
            {{ isEditMode ? 'Editar Proveedor' : 'Registrar Proveedor' }}
          </h5>
          <button type="button" class="btn-close btn-close-white" aria-label="Cerrar" @click="closeModal"></button>
        </div>
        <div class="modal-body">
          <form novalidate @submit.prevent="handleSave">
            <div v-if="formError" class="alert alert-danger" role="alert" data-test="error-formulario">
              {{ formError }}
            </div>

            <!-- SPEC_ALTA_PROVEEDOR_DNIT_Y_CAMPOS_OPCIONALES_v0.1.md §3: lo único obligatorio -->
            <fieldset class="bloque mb-3" data-test="bloque-proveedor">
              <legend class="bloque-titulo"><i class="fas fa-building me-2"></i>Proveedor</legend>
              <div class="row g-3">
                <div class="col-md-5">
                  <label for="provDocumento" class="form-label fw-bold">RUC / Documento <span class="text-danger">*</span></label>
                  <input
                    id="provDocumento"
                    ref="documentoInput"
                    v-model="form.documento"
                    type="text"
                    inputmode="numeric"
                    autocomplete="off"
                    maxlength="20"
                    class="form-control"
                    :class="{ 'is-invalid': validationErrors.documento }"
                    placeholder="Ej: 80012345-6"
                    @input="alCambiarDocumento"
                    @blur="alSalirDelDocumento('proveedor')"
                    @keydown.enter.prevent="alSalirDelDocumento('proveedor')"
                  />
                  <div v-if="validationErrors.documento" class="invalid-feedback d-block">{{ validationErrors.documento }}</div>
                  <small v-else class="text-muted">Al salir del campo se completa la razón social desde el padrón DNIT.</small>
                </div>
                <div class="col-md-7">
                  <label for="provNombre" class="form-label fw-bold">Razón social <span class="text-danger">*</span></label>
                  <input
                    id="provNombre"
                    v-model="form.nombre"
                    type="text"
                    maxlength="100"
                    class="form-control"
                    :class="{ 'is-invalid': validationErrors.nombre }"
                    placeholder="Nombre de la empresa o del proveedor"
                  />
                  <div v-if="validationErrors.nombre" class="invalid-feedback d-block">{{ validationErrors.nombre }}</div>
                </div>
              </div>

              <div class="mt-2" aria-live="polite">
                <div v-if="padron.consultando" class="form-text">Buscando en el padrón…</div>
                <div v-else-if="existente" class="alert alert-warning py-2 mb-0 d-flex flex-wrap align-items-center gap-2" data-test="ya-es-proveedor">
                  <span>Ya es proveedor: <strong>{{ existente.nombre || 'sin nombre' }}</strong> ({{ existente.nro_documento }}).</span>
                  <button type="button" class="btn btn-sm btn-outline-dark" @click="usarExistente">Usar este proveedor</button>
                </div>
                <div v-else-if="padron.aviso" class="form-text text-primary" data-test="aviso-padron">{{ padron.aviso }}</div>
              </div>
            </fieldset>

            <!-- RN-PV3: contacto principal, opcional -->
            <fieldset class="bloque mb-3" data-test="bloque-contacto">
              <legend class="bloque-titulo"><i class="fas fa-user me-2"></i>Contacto principal <span class="fw-normal text-muted">(opcional)</span></legend>
              <div class="row g-3">
                <div class="col-md-3">
                  <label for="provContactoDoc" class="form-label">Documento</label>
                  <input
                    id="provContactoDoc"
                    v-model="form.contacto_documento"
                    type="text"
                    inputmode="numeric"
                    autocomplete="off"
                    maxlength="20"
                    class="form-control"
                    placeholder="CI o RUC"
                    @input="alCambiarDocumentoContacto"
                    @blur="alSalirDelDocumento('contacto')"
                    @keydown.enter.prevent="alSalirDelDocumento('contacto')"
                  />
                </div>
                <div class="col-md-3">
                  <label for="provContacto" class="form-label">Nombre</label>
                  <input id="provContacto" v-model="form.contacto" type="text" class="form-control" placeholder="Nombre del contacto" />
                </div>
                <div class="col-md-3">
                  <label for="provCelular" class="form-label">Celular</label>
                  <input
                    id="provCelular"
                    v-model="form.telefono_celular"
                    type="tel"
                    maxlength="20"
                    class="form-control"
                    :class="{ 'is-invalid': validationErrors.telefono_celular }"
                    placeholder="0981 123 456"
                  />
                  <div v-if="validationErrors.telefono_celular" class="invalid-feedback d-block">{{ validationErrors.telefono_celular }}</div>
                </div>
                <div class="col-md-3">
                  <label for="provCorreo" class="form-label">Correo</label>
                  <input
                    id="provCorreo"
                    v-model.trim="form.email"
                    type="email"
                    maxlength="50"
                    class="form-control"
                    :class="{ 'is-invalid': validationErrors.email }"
                    placeholder="nombre@dominio.com"
                  />
                  <div v-if="validationErrors.email" class="invalid-feedback d-block">{{ validationErrors.email }}</div>
                </div>
              </div>
              <div v-if="contactoPadron.consultando" class="form-text mt-2">Buscando en el padrón…</div>
              <div v-else-if="contactoPadron.aviso" class="form-text text-primary mt-2">{{ contactoPadron.aviso }}</div>
            </fieldset>

            <!-- RN-PV4: el resto, plegado -->
            <button
              type="button"
              class="btn btn-link px-0 mb-2"
              :aria-expanded="mostrarMasDatos ? 'true' : 'false'"
              aria-controls="provMasDatos"
              data-test="toggle-mas-datos"
              @click="mostrarMasDatos = !mostrarMasDatos"
            >
              <i :class="mostrarMasDatos ? 'fas fa-chevron-down me-1' : 'fas fa-chevron-right me-1'"></i>
              Más datos (opcional)
            </button>
            <div v-show="mostrarMasDatos" id="provMasDatos" class="row g-3 mb-3" data-test="mas-datos">
              <div class="col-md-4">
                <label for="provFantasia" class="form-label">Nombre fantasía</label>
                <input id="provFantasia" v-model="form.nombre_fantasia" type="text" maxlength="100" class="form-control" placeholder="Nombre comercial" />
              </div>
              <div class="col-md-5">
                <label for="provDireccion" class="form-label">Dirección</label>
                <input
                  id="provDireccion"
                  v-model="form.direccion"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': validationErrors.direccion }"
                  placeholder="Calle, número, ciudad"
                />
                <div v-if="validationErrors.direccion" class="invalid-feedback d-block">{{ validationErrors.direccion }}</div>
              </div>
              <div class="col-md-3">
                <label for="provCondiciones" class="form-label">Condiciones de pago</label>
                <input id="provCondiciones" v-model="form.condiciones_pago" type="text" maxlength="100" class="form-control" placeholder="Ej: 30 días, Contado" />
              </div>
            </div>

            <div class="d-flex justify-content-end gap-2 mt-4">
              <button type="button" class="btn btn-outline-secondary" @click="closeModal">
                <i class="fas fa-times me-1"></i>Cancelar
              </button>
              <button type="submit" class="btn btn-success" :disabled="guardando">
                <i :class="isEditMode ? 'fas fa-edit me-1' : 'fas fa-save me-1'"></i>
                {{ isEditMode ? 'Actualizar Proveedor' : 'Guardar Proveedor' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade show" v-if="showModal"></div>
</template>

<script>
import apiService from '../services/apiService.js';
import {
  documentoConsultable,
  avisoPadron,
  formularioVacio,
  formularioDesdeProveedor,
  tieneMasDatos,
  aplicarPadronProveedor,
  aplicarPadronContacto,
  olvidarDelPadron,
  validarFormulario,
  armarPayload
} from '@/utils/padronProveedor.mjs';


export default {
  name: 'RegistrarProveedor',
  emits: ['update:showModal', 'provider-registered'],
  props: {
    title: { type: String, default: 'Registrar Proveedor' },
    initialRuc: { type: String, default: '' },
    initialNombre: { type: String, default: '' },
    id: { type: [String, Number], default: null },
    showModal: { type: Boolean, default: false }
  },
  data() {
    return {
      form: formularioVacio(),
      documentoOriginal: '',
      mostrarMasDatos: false,
      formError: '',
      validationErrors: {},
      guardando: false,
      existente: null,
      padron: { consultando: false, aviso: '' },
      contactoPadron: { consultando: false, aviso: '' },
      // Lo último que completó el padrón en cada campo (RN-PV10).
      delPadron: { nombre: null, contacto: null },
      // El documento cambió desde la última consulta (RN-PV11).
      sinConsultar: { proveedor: false, contacto: false }
    };
  },
  computed: {
    isEditMode() {
      return !!this.id;
    }
  },
  watch: {
    showModal: {
      handler(visible) {
        if (!visible) {
          this.cancelarConsultas();
          return;
        }
        this.reiniciarEstado();
        if (this.isEditMode) {
          this.loadProvider();
        } else {
          this.form = formularioVacio(this.initialRuc, this.initialNombre);
          this.documentoOriginal = '';
          this.mostrarMasDatos = false;
          // RN-PV8: abierto desde el selector de una compra con el RUC ya escrito.
          if (documentoConsultable(this.form.documento)) this.consultarPadron('proveedor', this.form.documento);
        }
      },
      immediate: true
    },
    id(nuevo) {
      if (nuevo && this.showModal) this.loadProvider();
    }
  },
  beforeUnmount() {
    this.cancelarConsultas();
  },
  methods: {
    reiniciarEstado() {
      this.cancelarConsultas();
      this.formError = '';
      this.validationErrors = {};
      this.existente = null;
      this.guardando = false;
      this.padron = { consultando: false, aviso: '' };
      this.contactoPadron = { consultando: false, aviso: '' };
      this.delPadron = { nombre: null, contacto: null };
      this.sinConsultar = { proveedor: false, contacto: false };
    },
    cancelarConsultas() {
      if (this.abortProveedor) this.abortProveedor.abort();
      if (this.abortContacto) this.abortContacto.abort();
    },
    // RN-PV11: mientras se escribe sólo se descarta lo viejo; la consulta va al
    // salir del campo (o con Enter), no a mitad de un RUC o una CI.
    alCambiarDocumento() {
      if (this.abortProveedor) this.abortProveedor.abort();
      this.padron = { consultando: false, aviso: '' };
      this.existente = null;
      this.form.ruc_padron = null;
      this.form.nombre = olvidarDelPadron(this.form.nombre, this.delPadron.nombre);
      this.delPadron.nombre = null;
      this.sinConsultar.proveedor = true;
    },
    alCambiarDocumentoContacto() {
      if (this.abortContacto) this.abortContacto.abort();
      this.contactoPadron = { consultando: false, aviso: '' };
      this.form.contacto = olvidarDelPadron(this.form.contacto, this.delPadron.contacto);
      this.delPadron.contacto = null;
      this.sinConsultar.contacto = true;
    },
    alSalirDelDocumento(ambito) {
      if (!this.sinConsultar[ambito]) return;
      this.sinConsultar[ambito] = false;
      const valor = ambito === 'contacto' ? this.form.contacto_documento : this.form.documento;
      if (documentoConsultable(valor)) this.consultarPadron(ambito, valor);
    },
    // RN-PV9 a RN-PV13. Cualquier falla del padrón degrada en silencio a la carga manual.
    async consultarPadron(ambito, documento) {
      const esContacto = ambito === 'contacto';
      const controlador = new AbortController();
      const estado = esContacto ? 'contactoPadron' : 'padron';
      if (esContacto) this.abortContacto = controlador; else this.abortProveedor = controlador;
      this[estado].consultando = true;
      try {
        const url = `/api/providers/padron/${encodeURIComponent(documento)}`;
        const { data } = await apiService.get(url, esContacto ? { ambito: 'contacto' } : {}, { signal: controlador.signal });
        const actual = esContacto ? this.form.contacto_documento : this.form.documento;
        if (controlador.signal.aborted || actual !== documento) return;
        const r = data?.data || {};
        if (!esContacto && r.origen === 'proveedor' && r.proveedor) {
          // En edición, el propio proveedor no es un duplicado.
          if (!this.isEditMode || String(r.proveedor.proveedor_id) !== String(this.id)) this.existente = r.proveedor;
        } else if (r.origen === 'padron' && r.proveedor) {
          if (esContacto) {
            const antes = this.form.contacto;
            this.form = aplicarPadronContacto(this.form, r.proveedor, { nombrePrevio: this.delPadron.contacto });
            if (this.form.contacto !== antes) this.delPadron.contacto = this.form.contacto;
            this.contactoPadron.aviso = avisoPadron(r.proveedor);
          } else {
            const antes = this.form.nombre;
            this.form = aplicarPadronProveedor(this.form, r.proveedor, { nombrePrevio: this.delPadron.nombre });
            if (this.form.nombre !== antes) this.delPadron.nombre = this.form.nombre;
            this.padron.aviso = avisoPadron(r.proveedor);
          }
        }
      } catch (error) {
        // 404 = no está en el padrón: carga manual normal. Lo demás, silencio (RN-PV12).
      } finally {
        if ((esContacto ? this.abortContacto : this.abortProveedor) === controlador) this[estado].consultando = false;
      }
    },
    // RN-PV14b: desde una compra, deja seleccionado el proveedor que ya existe.
    usarExistente() {
      if (!this.existente) return;
      this.$emit('provider-registered', { ...this.existente });
      this.$emit('update:showModal', false);
    },
    async handleSave() {
      this.formError = '';
      this.validationErrors = validarFormulario(this.form);
      if (Object.keys(this.validationErrors).length) {
        this.formError = Object.values(this.validationErrors)[0];
        return;
      }
      const payload = armarPayload(this.form, { editMode: this.isEditMode, documentoOriginal: this.documentoOriginal });
      this.guardando = true;
      try {
        const response = this.isEditMode
          ? await apiService.put(`/api/providers/${this.id}`, payload)
          : await apiService.post('/api/providers/', payload);
        this.$emit('provider-registered', response.data);
        this.$emit('update:showModal', false);
      } catch (error) {
        const datos = error?.response?.data || {};
        if (error?.response?.status === 409 && datos.code === 'proveedor_duplicado' && datos.proveedor) {
          this.existente = datos.proveedor;
          this.formError = datos.message;
        } else if (error?.response?.status === 400 && datos.errores) {
          this.validationErrors = datos.errores;
          this.formError = datos.message;
        } else {
          this.formError = datos.message || error?.userMessage
            || `No se pudo ${this.isEditMode ? 'actualizar' : 'registrar'} el proveedor.`;
        }
      } finally {
        this.guardando = false;
      }
    },
    closeModal() {
      this.$emit('update:showModal', false);
    },
    async loadProvider() {
      try {
        const { data } = await apiService.get(`/api/providers/${this.id}`);
        this.form = formularioDesdeProveedor(data);
        this.documentoOriginal = this.form.documento;
        this.mostrarMasDatos = tieneMasDatos(this.form);
      } catch (error) {
        this.formError = error?.userMessage || 'No se pudo cargar el proveedor.';
      }
    }
  }
};
</script>

<style scoped>
.modal-xl {
  max-width: 1200px;
}

.modal-body {
  max-height: 75vh;
  overflow-y: auto;
  padding: 1.5rem;
}

.bloque {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 0.75rem 1rem 1rem;
}

.bloque-titulo {
  float: none;
  width: auto;
  padding: 0 0.4rem;
  margin-bottom: 0.5rem;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--bs-success, #198754);
}

.form-label.fw-bold {
  font-size: 0.9rem;
  color: #333;
}

.btn-close-white {
  filter: invert(1) grayscale(100%) brightness(200%);
}

@media (max-width: 768px) {
  .modal-xl {
    max-width: 95%;
    margin: 1rem auto;
  }

  .modal-body {
    padding: 1rem;
  }
}
</style>
